/**
 * Projects Form
 */

import { pushNotification } from '../../../redux/actions/notifications';
import { requestDeleteProject, requestUpdateProject } from '../../../redux/actions/projects';

import { difference, isMatch } from 'lodash';
import {
  arrayMove,
  arrayRemove,
  arraySplice,
  change,
  formValueSelector,
  initialize,
  reduxForm,
  submit,
} from 'redux-form';

import { ProjectsPageProps } from './';

export interface IFormFields {
  activeProject?: IActiveProjectProps;
  createProject?: string;
  projects: IProjectItemProps[];
}

interface IActiveProjectProps {
  id?: string;
  isPublic: boolean;
  name: string;
  tracks: ITrackItemProps[];
}

export interface ITrackItemProps {
  id: string;
  association_id: string;
}
export interface IProjectItemProps {
  id: string;
  title: string;
  subtitle: string;
  isPublic: boolean;
  userIsOwner: boolean;
}

const defaultList = [];
const form = 'projectForm';
export const selector = formValueSelector<IStoreState>(form);
export const reinitializeForm = (data: IFormFields) => initialize(form, data);
export const submitForm = () => submit(form);
export const addProject = () => change(form, 'createProject', '');
export const removeProject = (index: number) => arrayRemove(form, 'projects', index);
export const replaceProject = (index: number, replace: IProjectItemProps) =>
  arraySplice(form, 'projects', index, 1, replace);
export const shareProject = () => change(form, 'activeProject.isPublic', true);
export const removeTrack = (index: number) => arrayRemove(form, 'activeProject.tracks', index);
export const moveTrack = (oldIndex: number, newIndex: number) =>
  arrayMove(form, 'activeProject.tracks', oldIndex, newIndex);

export const ProjectsPageReduxForm = reduxForm<IFormFields, any>({
  form,
  onSubmit: (values, dispatch, props: ProjectsPageProps) => new Promise((resolve, reject) => {
    const nextProjects = values.projects.map(({ id }) => id);
    const prevProjects = props.viewer.projects!.edges!.map(({ node }) => node!.id);
    const removedProject = difference(prevProjects, nextProjects);

    if (removedProject.length) {
    // Delete Project
      dispatch(requestDeleteProject({
        input: { id: removedProject[0] },
        promise: { resolve, reject },
      }));
    } else {
    // Update Project
      const activeProject = props.viewer.node as GQL.IProject;
      const mutationInput: GQL.IUpdateProjectInput = {
        id: activeProject.id,
      };

      const nextName = values.activeProject!.name;
      const prevName = activeProject.name;

      const tracksMutation: GQL.IUpdateProjectTrackInput = {};
      const nextTracks = values.activeProject!.tracks!.map(({ association_id }) => association_id);
      const prevTracks = activeProject.tracks!.edges!.map(({ id }) => id);
      const removedTracks = difference(prevTracks, nextTracks);
      const trackOrderChanged = !isMatch(prevTracks, nextTracks);

      let shouldPerformMutation = false;

      const updateProjectInList = (updatedValues: Partial<IProjectItemProps>) => {
        const projectIndex = values.projects.findIndex(({ id }) => id === values.activeProject!.id);
        const updatedProject: IProjectItemProps = {
          ...values.projects[projectIndex],
          ...updatedValues,
        };
        dispatch(replaceProject(projectIndex, updatedProject));
      };

      if (nextName !== prevName) {
        shouldPerformMutation = true;
        mutationInput.name = nextName;
        updateProjectInList({ title: nextName });
      } else if (removedTracks.length) {
        shouldPerformMutation = true;
        tracksMutation.remove = removedTracks.map((id) => ({ id: id! }));
      } else if (trackOrderChanged) {
        shouldPerformMutation = true;
        tracksMutation.update = nextTracks.map((id) => ({
          id: id!,
        }));
      } else if (values.activeProject!.isPublic && !activeProject.is_public) {
        shouldPerformMutation = true;
        mutationInput.is_public = true;
        updateProjectInList({ isPublic: true });
      }

      mutationInput.tracks = tracksMutation;

      if (shouldPerformMutation) {
        dispatch(requestUpdateProject({
          input: mutationInput,
          promise: { resolve, reject },
        }));
      } else {
        resolve();
      }
    }
  }),

  onSubmitFail: (errors, dispatch, submitError, props: ProjectsPageProps) => {
    const activeProject = props.viewer.node as GQL.IProject;
    const allProjects = props.viewer.projects!.edges!.map(({ node }) => node!);
    dispatch(reinitializeForm({
      activeProject: generateActiveProjectItemProps(activeProject),
      projects: generateProjectItemsProps(allProjects, props.userProfile),
    }));
    dispatch(pushNotification({
      autoDismiss: false,
      message: 'Something went wront while updating playlist. Please try again later or contact us.',
      state: 'error',
    }));
  },
});

function generateProjectItemProps(
  project: GQL.IProject,
  userProfile: GQL.IProfile | undefined,
): IProjectItemProps {
  return {
    id: project.id,
    isPublic: !!project.is_public,
    title: project.name,
    subtitle: `${project.tracks && project.tracks.edges!.length ? project.tracks.edges!.length : 'No'} tracks`,
    userIsOwner: !project.owner || !!userProfile && userProfile.user_id === project.owner!.user_id,
  };
}

export function generateActiveProjectItemProps(project: GQL.IProject): IActiveProjectProps {
  return {
    id: project.id,
    isPublic: !!project.is_public,
    name: project.name,
    tracks: project.tracks ?
      project.tracks.edges!.map(({ id: associationId, node }) => ({ id: node!.id, association_id: associationId })) :
      defaultList,
  };
}

export function generateProjectItemsProps(projects: GQL.IProject[], userProfile: GQL.IProfile | undefined) {
  return projects.map((project) => generateProjectItemProps(project, userProfile));
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Projects/form.tsx